"use client";

import * as SwitchPrimitive from "@radix-ui/react-switch";
import { type ComponentPropsWithoutRef, type ComponentRef, forwardRef } from "react";

import { cn } from "@/lib/utils";

/* ── Size config (Figma spec) ──
 * large: track 48×28, thumb 20px, padding 4px
 * small: track 34×20, thumb 16px, padding 2px
 */
const SIZE_CONFIG = {
  large: {
    track: "w-12 h-7",          // 48×28
    thumb: "w-5 h-5 translate-x-1 data-[state=checked]:translate-x-6",
  },
  small: {
    track: "w-[34px] h-5",      // 34×20
    thumb: "w-4 h-4 translate-x-0.5 data-[state=checked]:translate-x-4",
  },
} as const;

type SwitchSize = keyof typeof SIZE_CONFIG;

export interface ToggleSwitchProps
  extends ComponentPropsWithoutRef<typeof SwitchPrimitive.Root> {
  size?: SwitchSize;
}

const ToggleSwitch = forwardRef<
  ComponentRef<typeof SwitchPrimitive.Root>,
  ToggleSwitchProps
>(({ className, size = "large", ...props }, ref) => {
  const s = SIZE_CONFIG[size];

  return (
    <SwitchPrimitive.Root
      ref={ref}
      className={cn(
        "peer inline-flex shrink-0 rounded-full",
        s.track,
        "bg-gray-400",
        "data-[state=checked]:bg-primary-700",
        "disabled:opacity-50 disabled:cursor-default",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-700/50",
        "transition-colors duration-200",
        "cursor-pointer relative",
        className,
      )}
      {...props}
    >
      <SwitchPrimitive.Thumb
        className={cn(
          "block rounded-full bg-white",
          "absolute top-1/2 -translate-y-1/2",
          "transition-transform duration-200",
          "shadow-depth200",
          s.thumb,
        )}
      />
    </SwitchPrimitive.Root>
  );
});
ToggleSwitch.displayName = "ToggleSwitch";

export { ToggleSwitch };
